import BUILDING_NODES from 'lucide-nodes:building-2'
import type { Map as MapboxMap } from 'mapbox-gl'

import { WORKFLOW_LABEL_PLATE } from '~/client/src/shared/map/labelPlateSpec'
import {
  bakedScaleForBand,
  bandOfImageId,
  withoutImageBand,
} from '~/client/src/shared/map/mapZoomRule'
import { isMostlyWhiteImageData } from '~/client/src/shared/utils/imageWhiteness'

import { drawLabelPlate, labelPlateGeometry } from './GLLabelPlateImage'
import { DEFAULT_GLYPH_STROKE_WIDTH, iconNodesToPath2Ds } from './iconGeometry'

import Colors from '~/client/src/shared/theme.module.scss'

export const COMPANY_LOGO_KEY_PREFIX = 'co-logo-'
// The chip of a company with no logo, so every label wears one: an unchipped label starts a
// whole chip lead closer to its pin and loses to it. Drawn here, not fetched.
export const DEFAULT_COMPANY_LOGO_IMAGE_ID = `${COMPANY_LOGO_KEY_PREFIX}default`

// A plated label carries its chip INSIDE the plate, and a symbol has one icon-image — so the
// plate and the chip have to be the same image. Same logo, same registry, same retry state
// machine; only the geometry and one extra fill differ.
export const PLATE_SUFFIX = '-plate'

function isPlated(imageId: string): boolean {
  return imageId.endsWith(PLATE_SUFFIX)
}

// `companylogo-<id>` is what a feature carries; the layer concatenates the zoom band, and the
// plated variant appends its suffix after that — so both are stripped to get back to the id the
// URL registry is keyed by.
function logoIdOf(imageId: string): string {
  const unplated = isPlated(imageId)
    ? imageId.slice(0, -PLATE_SUFFIX.length)
    : imageId
  return withoutImageBand(unplated)
}

// The band a variant was requested for, so its fixed material bakes at that band's size.
function bandOf(imageId: string): number | null {
  return bandOfImageId(
    isPlated(imageId) ? imageId.slice(0, -PLATE_SUFFIX.length) : imageId,
  )
}

// The 9-patch a variant is drawn into. Chip and plate share everything downstream of this.
function variantGeometry(imageId: string, ratio: number) {
  // Everything baked into this raster — the chip, the plate's corners, the lead — is drawn at
  // the band's scale. The label's type shrinks on the same ramp, so the chip keeps exactly the
  // proportion to the text that it has at full zoom.
  const scale = bakedScaleForBand(bandOf(imageId))
  if (!isPlated(imageId)) {
    const g = chipImageGeometry(ratio, scale)
    return {
      width: g.width,
      height: g.height,
      size: g.size,
      top: g.band,
      left: 0,
      metadata: g.metadata,
    }
  }
  const g = labelPlateGeometry(ratio, WORKFLOW_LABEL_PLATE, scale)
  const band = g.metadata.stretchY[0][1]
  return {
    width: g.width,
    height: g.height,
    size: Math.round(WORKFLOW_LABEL_PLATE.chipPx * scale * ratio),
    top: band,
    left: Math.round(WORKFLOW_LABEL_PLATE.padXPx * scale * ratio),
    metadata: g.metadata,
    plate: g,
  }
}

export function getCompanyLogoImageId(companyId: string): string {
  return `${COMPANY_LOGO_KEY_PREFIX}${companyId}`
}

// 20px chip (Figma's 16px bumped for on-map legibility). Literal hex — GL can't read theme
// tokens; chip is fixed light.
const CHIP_PX = 20
const CHIP_GAP_PX = 4
// The chip ships as a stretchable image so icon-text-fit can grow it around the label:
// [chip][gap][content] across, [band][chip][band] down. Only the content column and the two
// bands stretch, so the chip holds 20px and stays vertically centred however the text wraps.
//
// A fitted 9-patch can follow the type's position but NOT its scale: mapbox emits fixed regions
// as unscaled pixel offsets and stretch regions as em offsets, then floors the em half by
// minFontScaleY — the ratio of the image's fixed height to the box it is fitted into. When that
// ratio reaches 1 the chip's offsets stop shrinking while the glyphs keep scaling, and the text
// rides high in the chip on a pitched map.
//
// The invariant below removes the floor rather than compensating for it: a symbol's perspective
// ratio is always above 0.5, so fitting into at least twice the fixed height can never bind.
const STRETCH_BAND_PX = 8
const CONTENT_MIN_PX = 2
const CHIP_LEAD_PX = CHIP_PX + CHIP_GAP_PX

// What a layer fitting this image to its text has to know. Derived here so a change to the
// patch layout reaches the fit math instead of silently disagreeing with it.
// Fields are ILabelPlateFit's; see there. The chip takes the STRICT floor rather than the
// plate's measured one — its clamp padding is invisible transparent band, so it can afford to.
export const CHIP_LABEL_FIT = {
  leadPx: CHIP_LEAD_PX,
  fixedContentHeightPx: CHIP_PX,
  minFitHeightPx: 2 * CHIP_PX,
} as const

// One source for the whole 9-patch: every part is rounded once and the image is sized from
// the rounded parts, never rounded separately. Rounding them independently lets a band round
// to zero below 1x (browser zoom), which makes the stretch total zero and the fit divide by
// it. Bands and the content column are floored at a pixel for the same reason.
function chipImageGeometry(ratio: number, scale = 1) {
  const band = Math.max(1, Math.round(STRETCH_BAND_PX * scale * ratio))
  const size = Math.round(CHIP_PX * scale * ratio)
  const lead = Math.round(CHIP_LEAD_PX * scale * ratio)
  const content = Math.max(1, Math.round(CONTENT_MIN_PX * ratio))
  const width = lead + content
  const height = band * 2 + size
  return {
    width,
    height,
    size,
    band,
    metadata: {
      pixelRatio: ratio,
      stretchX: [[lead, width]] as Array<[number, number]>,
      stretchY: [
        [0, band],
        [band + size, height],
      ] as Array<[number, number]>,
      content: [lead, 0, width, height] as [number, number, number, number],
    },
  }
}
const CHIP_RADIUS_PX = 2
const CHIP_PADDING_PX = 2
const CHIP_FILL = '#ffffff'
const CHIP_BORDER = '#e4e5e8'
// A mostly-white logo disappears on the white card — those get a grey chip.
const CHIP_FILL_MUTED = '#e4e5e8'
const CHIP_BORDER_MUTED = '#d4d6da'

// Downscale to a fixed box before sampling: SVGs without an intrinsic size draw
// nothing at native dimensions, and a small box keeps the scan cheap.
const WHITE_SAMPLE_PX = 24

function isMostlyWhiteLogo(img: HTMLImageElement): boolean {
  const canvas = document.createElement('canvas')
  canvas.width = WHITE_SAMPLE_PX
  canvas.height = WHITE_SAMPLE_PX
  const ctx = canvas.getContext('2d')
  if (!ctx) return false
  ctx.drawImage(img, 0, 0, WHITE_SAMPLE_PX, WHITE_SAMPLE_PX)
  const { data } = ctx.getImageData(0, 0, WHITE_SAMPLE_PX, WHITE_SAMPLE_PX)
  return isMostlyWhiteImageData(data)
}

// The image key can't carry a URL, so the label layer feeds this registry and
// the styleimagemissing handler resolves ids through it.
const logoUrlByImageId = new Map<string, string>()

export function registerCompanyLogoUrls(entries: Map<string, string>): void {
  entries.forEach((url, imageId) => logoUrlByImageId.set(imageId, url))
}

// Blacklist a logo only after a few failures: styleimagemissing re-fires every
// frame, but a transient blip shouldn't kill it for the session.
const MAX_LOGO_FETCH_ATTEMPTS = 3
// Per map: a fetch in flight for one map must not turn away another map's request, which would
// get no placeholder and drop the symbol, text included.
const pendingChipsByMap = new WeakMap<MapboxMap, Map<string, Promise<void>>>()
const failedImageIds = new Set<string>()
// Ids whose real pixels actually landed. `map.hasImage` cannot answer this — the placeholder
// below is a fully transparent image of the right SIZE, so it reads as present from the moment
// the fetch starts, and a lost swap leaves that transparent image protected forever.
const resolvedImageIds = new Set<string>()
const failedAttemptsByImageId = new Map<string, number>()

// Both variants of a logo draw the same bitmap, so it is fetched, decoded and scanned once.
// Dropped on rejection: the cached promise would otherwise fail every retry below.
const decodedByUrl = new Map<
  string,
  Promise<{ img: HTMLImageElement; muted: boolean }>
>()

// NOT img.decode(): Chrome defers decoding in a hidden tab and that promise never settles,
// neither resolving nor rejecting. Every guard here assumes a promise that finishes, so a hung
// decode wedges the id as pending for the life of the page — a map opened in a background tab
// lost its chips permanently. `load` fires in a hidden tab; the timeout is the belt, since a
// stalled request has to REJECT for the attempt budget to retry it.
const LOGO_LOAD_TIMEOUT_MS = 15000

function loadLogo(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    // Logos live on Firebase/GCS; without CORS the canvas taints on getImageData.
    img.crossOrigin = 'anonymous'
    const timer = window.setTimeout(() => {
      img.src = ''
      reject(new Error(`logo load timed out: ${url}`))
    }, LOGO_LOAD_TIMEOUT_MS)
    img.onload = () => {
      window.clearTimeout(timer)
      resolve(img)
    }
    img.onerror = () => {
      window.clearTimeout(timer)
      reject(new Error(`logo load failed: ${url}`))
    }
    img.src = url
  })
}

function decodeLogo(
  url: string,
): Promise<{ img: HTMLImageElement; muted: boolean }> {
  const cached = decodedByUrl.get(url)
  if (cached) return cached
  const decoding = loadLogo(url)
    .then(img => ({ img, muted: isMostlyWhiteLogo(img) }))
    .catch(error => {
      decodedByUrl.delete(url)
      throw error
    })
  decodedByUrl.set(url, decoding)
  return decoding
}

// The chip's frame, with whatever it holds drawn into the padded box inside it. Same geometry
// as the placeholder, since updateImage requires identical dimensions.
function renderChip(
  imageId: string,
  ratio: number,
  muted: boolean,
  drawContent: (
    ctx: CanvasRenderingContext2D,
    box: { left: number; top: number; size: number },
  ) => void,
): ImageData | null {
  const variant = variantGeometry(imageId, ratio)
  const { width, height, size, top, left } = variant
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  // The plate goes down first; the chip then sits inside it.
  if (variant.plate) {
    drawLabelPlate(ctx, variant.plate, WORKFLOW_LABEL_PLATE)
  }

  const radius = CHIP_RADIUS_PX * ratio
  const border = 1 * ratio
  const chip = new Path2D()
  chip.roundRect(
    left + border / 2,
    top + border / 2,
    size - border,
    size - border,
    radius,
  )
  ctx.fillStyle = muted ? CHIP_FILL_MUTED : CHIP_FILL
  ctx.fill(chip)

  // Clip so the content can't spill past the rounded corners.
  ctx.save()
  ctx.clip(chip)
  const padding = CHIP_PADDING_PX * ratio
  drawContent(ctx, {
    left: left + padding,
    top: top + padding,
    size: size - padding * 2,
  })
  ctx.restore()

  ctx.lineWidth = border
  ctx.strokeStyle = muted ? CHIP_BORDER_MUTED : CHIP_BORDER
  ctx.stroke(chip)

  return ctx.getImageData(0, 0, width, height)
}

// ratio comes from the caller so the raster's dimensions match the placeholder
// it replaces (updateImage requires identical size).
async function renderCompanyLogoChip(
  url: string,
  ratio: number,
  imageId: string,
): Promise<ImageData | null> {
  const { img, muted } = await decodeLogo(url)
  return renderChip(imageId, ratio, muted, (ctx, inner) => {
    // A viewBox-only SVG reports 0, which scales to NaN and draws nothing.
    const naturalW = img.naturalWidth || inner.size
    const naturalH = img.naturalHeight || inner.size
    const scale = Math.min(inner.size / naturalW, inner.size / naturalH)
    const w = naturalW * scale
    const h = naturalH * scale
    ctx.drawImage(
      img,
      inner.left + (inner.size - w) / 2,
      inner.top + (inner.size - h) / 2,
      w,
      h,
    )
  })
}

// Lucide's 24-unit grid, stroked like every other glyph the map draws. Path2D rather than an
// SVG image: an SVG data URI drawn to a canvas taints it on WebKit.
const LUCIDE_GRID = 24

function renderDefaultCompanyChip(
  imageId: string,
  ratio: number,
): ImageData | null {
  return renderChip(imageId, ratio, false, (ctx, inner) => {
    const scale = inner.size / LUCIDE_GRID
    ctx.translate(inner.left, inner.top)
    ctx.scale(scale, scale)
    ctx.strokeStyle = Colors.mapLabelMutedOnLight
    ctx.lineWidth = DEFAULT_GLYPH_STROKE_WIDTH
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    iconNodesToPath2Ds(BUILDING_NODES).forEach(path => ctx.stroke(path))
  })
}

// Both variants, whichever the map asked for. A plate is a DISTINCT image id, requested only
// the first time a plated label lays out — and a symbol missing its icon-image at layout time
// is never re-placed when the image lands, since addImage + triggerRepaint do not re-run
// placement. Registering the sibling removes the race instead of racing it.
export function registerMissingCompanyLogoImage(
  map: MapboxMap,
  imageId: string,
  withPlate = true,
): void {
  if (!imageId.startsWith(COMPANY_LOGO_KEY_PREFIX)) return
  // Both variants of the SAME band: the sibling removes the first-swap race, and the band stops
  // a plated label borrowing the chip baked for a different zoom.
  //
  // The plate is the expensive half by a wide margin and most labels never wear one, so a caller
  // warming ahead of demand can pay for the chips only. Asking for a plate by name always gets
  // one, which is the path a label takes the moment it does plate.
  const banded = isPlated(imageId)
    ? imageId.slice(0, -PLATE_SUFFIX.length)
    : imageId
  registerLogoVariant(map, banded)
  if (withPlate || isPlated(imageId)) {
    registerLogoVariant(map, banded + PLATE_SUFFIX)
  }
}

function registerLogoVariant(map: MapboxMap, imageId: string): void {
  // One live fetch per id and map. This is the one record a style cannot invalidate — it
  // describes this module's own in-flight work rather than anything the map holds — so it is
  // checked alone.
  // Under the style conjunction below it would stop guarding exactly across a style swap, and a
  // fetch straddling the swap would rasterise the same chip, and its far more expensive plate,
  // twice on the frame budget of the swap.
  const pendingChips =
    pendingChipsByMap.get(map) ?? new Map<string, Promise<void>>()
  pendingChipsByMap.set(map, pendingChips)
  if (pendingChips.has(imageId)) {
    return
  }
  // The records outlive any style, the rasters do not: setStyle drops every image added below,
  // so "already handled" has to mean "handled in THIS style". Both halves are load-bearing —
  // hasImage alone protects a stranded placeholder after HMR, the records alone break across a
  // style swap.
  if (resolvedImageIds.has(imageId) || failedImageIds.has(imageId)) {
    if (map.hasImage(imageId)) {
      return
    }
    // A fresh style is a fresh chance: the attempt budget counts fetches into a style that no
    // longer exists, so a logo that blipped twice before the switch still gets its three tries.
    failedImageIds.delete(imageId)
    failedAttemptsByImageId.delete(imageId)
  }
  const ratio = window.devicePixelRatio || 1
  // Drawn, not fetched: added at once, which styleimagemissing accepts mid-event.
  if (logoIdOf(imageId) === DEFAULT_COMPANY_LOGO_IMAGE_ID) {
    const imageData = map.hasImage(imageId)
      ? null
      : renderDefaultCompanyChip(imageId, ratio)
    if (imageData) {
      try {
        map.addImage(
          imageId,
          imageData,
          variantGeometry(imageId, ratio).metadata,
        )
      } catch {
        // Style mid-reload — the styleimagemissing fallback will retry.
      }
    }
    return
  }
  const url = logoUrlByImageId.get(logoIdOf(imageId))
  if (!url) return

  // Register a transparent chip-sized placeholder SYNCHRONOUSLY: the fetch takes
  // longer than the first tile render, so without it GL logs a "could not be
  // loaded" warning per tile request. Same dimensions as the final chip, so the
  // later updateImage swaps pixels in place — no re-layout, no re-placement.
  const { width, height, metadata } = variantGeometry(imageId, ratio)
  try {
    // Only when nothing holds the id. A stranded placeholder from an earlier attempt is left in
    // place and re-rendered below, so it can heal instead of being guarded forever.
    if (!map.hasImage(imageId)) {
      map.addImage(
        imageId,
        { width, height, data: new Uint8Array(width * height * 4) },
        metadata,
      )
    }
  } catch {
    // Style mid-reload — the styleimagemissing fallback will retry.
    return
  }

  const chip: Promise<void> = renderCompanyLogoChip(url, ratio, imageId)
    .then(imageData => {
      if (!imageData) {
        failedImageIds.add(imageId)
        return
      }
      try {
        if (map.hasImage(imageId)) {
          map.updateImage(imageId, imageData)
        } else {
          // Style reload cleared the placeholder mid-fetch.
          map.addImage(
            imageId,
            imageData,
            variantGeometry(imageId, ratio).metadata,
          )
        }
        resolvedImageIds.add(imageId)
        map.triggerRepaint()
      } catch {
        // Map removed or style swapped mid-flight. Deliberately NOT marked resolved, so the
        // next request for this id re-renders it rather than inheriting the blank placeholder.
      }
    })
    .catch(() => {
      const attempts = (failedAttemptsByImageId.get(imageId) ?? 0) + 1
      failedAttemptsByImageId.set(imageId, attempts)
      if (attempts >= MAX_LOGO_FETCH_ATTEMPTS) {
        // Permanent: keep the transparent placeholder so GL stays quiet.
        failedImageIds.add(imageId)
        return
      }
      // Transient: free the id — the placeholder would block the retry
      // (hasImage guard), and removing it lets styleimagemissing re-fire.
      try {
        if (map.hasImage(imageId)) map.removeImage(imageId)
      } catch {
        // Map removed mid-flight — nothing to free.
      }
    })
    .finally(() => pendingChips.delete(imageId))
  pendingChips.set(imageId, chip)
}

export function whenLogoChipsSettled(map: MapboxMap): Promise<void> {
  return Promise.allSettled([
    ...(pendingChipsByMap.get(map)?.values() ?? []),
  ]).then(() => undefined)
}
